import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBase } from '../apiBase';
import { authFetch, getUsername, isCollector, mustUseTodayRecordDate } from '../auth';
import { modalPanelClass } from './tableToolbar';
import {
  normalizePaymentReceiptInput,
  suggestNextPaymentReceiptNumber,
} from './paymentReceipt';
import {
  buildCustomerOutstandingBills,
} from './pendingBills';
import { getPaymentCheques, getPaymentCdmDeposits, getPaymentOnlineTransfers } from './paymentCheques';
import { SRI_LANKA_BANKS, bankCodeForName } from './sriLankaBanks';
import { useCollectorCollectionClosed } from './collectionDayClose';

const apiBase = getApiBase();

function money(n) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'LKR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

let chequeKeySeq = 0;
function newChequeLine(overrides = {}) {
  chequeKeySeq += 1;
  return {
    key: `chq-${chequeKeySeq}`,
    amount: '',
    chequeDate: todayYmdLocal(),
    chequeNumber: '',
    chequeBank: '',
    chequeBankCode: '',
    chequeBranchCode: '',
    ...overrides,
  };
}

let cdmKeySeq = 0;
function newCdmLine(overrides = {}) {
  cdmKeySeq += 1;
  return {
    key: `cdm-${cdmKeySeq}`,
    amount: '',
    cdmNumber: '',
    cdmDate: todayYmdLocal(),
    bankAccountId: '',
    ...overrides,
  };
}

let onlineTransferKeySeq = 0;
function newOnlineTransferLine(overrides = {}) {
  onlineTransferKeySeq += 1;
  return {
    key: `ot-${onlineTransferKeySeq}`,
    amount: '',
    reference: '',
    transferDate: todayYmdLocal(),
    bankAccountId: '',
    ...overrides,
  };
}

function bankAccountOptionLabel(a) {
  const nick = String(a.nickName ?? '').trim() || 'Account';
  const detail = [a.bank, a.accountNumber].map((x) => String(x ?? '').trim()).filter(Boolean).join(' · ');
  return detail ? `${nick} — ${detail}` : nick;
}

function lastChequeDefaultsForCustomer(payments, customerId) {
  const cid = String(customerId ?? '').trim();
  if (!cid) return null;
  const sorted = (Array.isArray(payments) ? payments : [])
    .filter((p) => String(p.customerId ?? '').trim() === cid)
    .sort((a, b) =>
      String(b.createdAt || `${b.date}T23:59:59`).localeCompare(
        String(a.createdAt || `${a.date}T23:59:59`),
      ),
    );
  for (const payment of sorted) {
    const cheques = getPaymentCheques(payment);
    if (cheques.length === 0) continue;
    const last = cheques[cheques.length - 1];
    return {
      chequeBank: last.chequeBank || '',
      chequeBankCode: last.chequeBankCode || '',
      chequeBranchCode: last.chequeBranchCode || '',
      chequeDate: last.chequeDate || todayYmdLocal(),
    };
  }
  return null;
}

function lastOtherMethodBankDefaults(payments, customerId) {
  const cid = String(customerId ?? '').trim();
  if (!cid) return { cdmBankAccountId: '', onlineTransferBankAccountId: '' };
  const sorted = (Array.isArray(payments) ? payments : [])
    .filter((p) => String(p.customerId ?? '').trim() === cid)
    .sort((a, b) =>
      String(b.createdAt || `${b.date}T23:59:59`).localeCompare(
        String(a.createdAt || `${a.date}T23:59:59`),
      ),
    );
  let cdmBankAccountId = '';
  let onlineTransferBankAccountId = '';
  for (const payment of sorted) {
    if (!cdmBankAccountId) {
      const deposits = getPaymentCdmDeposits(payment);
      const last = deposits[deposits.length - 1];
      if (last?.bankAccountId) cdmBankAccountId = last.bankAccountId;
    }
    if (!onlineTransferBankAccountId) {
      const transfers = getPaymentOnlineTransfers(payment);
      const last = transfers[transfers.length - 1];
      if (last?.bankAccountId) onlineTransferBankAccountId = last.bankAccountId;
    }
    if (cdmBankAccountId && onlineTransferBankAccountId) break;
  }
  return { cdmBankAccountId, onlineTransferBankAccountId };
}

const emptyForm = (receiptNumber = '') => ({
  customerId: '',
  billNumber: receiptNumber,
  cashAmount: '',
  cdmDeposits: [newCdmLine()],
  onlineTransfers: [newOnlineTransferLine()],
  cheques: [newChequeLine()],
  billAllocations: {},
  date: todayYmdLocal(),
  note: '',
});

const inputClass =
  'mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35';

export default function CollectorSeparateBillSettlementModal({
  open,
  onClose,
  onSaved,
  prefillCustomerId = '',
  lockCustomer = false,
  customerName = '',
}) {
  const receiptNumberTouched = useRef(false);
  const [step, setStep] = useState(1);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const paymentDay = useMemo(() => todayYmdLocal(), []);
  const { closed: collectionClosed } = useCollectorCollectionClosed(paymentDay);
  const newPaymentsLocked = isCollector() && collectionClosed;

  const loadBills = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/bills`);
      if (!res.ok) throw new Error('Failed to load bills');
      const data = await res.json();
      setBills(Array.isArray(data) ? data : []);
    } catch {
      setBills([]);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/payments`);
      if (!res.ok) throw new Error('Failed to load payments');
      const data = await res.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      setPayments([]);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/shop`);
      if (!res.ok) throw new Error('Failed to load shop');
      const data = await res.json();
      setBankAccounts(Array.isArray(data?.bankAccounts) ? data.bankAccounts : []);
    } catch {
      setBankAccounts([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    receiptNumberTouched.current = false;
    setStep(1);
    setSaveError(null);
    loadCustomers();
    loadBills();
    loadPayments();
    loadBankAccounts();
  }, [open, loadCustomers, loadBills, loadPayments, loadBankAccounts]);

  useEffect(() => {
    if (!open) return;
    const defaults = prefillCustomerId
      ? lastChequeDefaultsForCustomer(payments, prefillCustomerId)
      : null;
    const bankDefaults = prefillCustomerId
      ? lastOtherMethodBankDefaults(payments, prefillCustomerId)
      : { cdmBankAccountId: '', onlineTransferBankAccountId: '' };
    setForm({
      ...emptyForm(suggestNextPaymentReceiptNumber(payments)),
      customerId: prefillCustomerId || '',
      cdmDeposits: [newCdmLine({ bankAccountId: bankDefaults.cdmBankAccountId })],
      onlineTransfers: [newOnlineTransferLine({ bankAccountId: bankDefaults.onlineTransferBankAccountId })],
      cheques: [newChequeLine(defaults || {})],
    });
  }, [open, prefillCustomerId, payments]);

  useEffect(() => {
    if (!open || receiptNumberTouched.current) return;
    const next = suggestNextPaymentReceiptNumber(payments);
    setForm((f) => (f.billNumber === next ? f : { ...f, billNumber: next }));
  }, [open, payments]);

  const pendingBills = useMemo(() => {
    if (!form.customerId) return [];
    return [...buildCustomerOutstandingBills(customers, bills, payments, form.customerId)].sort((a, b) => {
      if (Boolean(a.isOpeningBalance) !== Boolean(b.isOpeningBalance)) {
        return a.isOpeningBalance ? -1 : 1;
      }
      const dateCmp = String(a.billDate).localeCompare(String(b.billDate));
      if (dateCmp !== 0) return dateCmp;
      return String(a.invoiceNumber || a.id).localeCompare(String(b.invoiceNumber || b.id));
    });
  }, [form.customerId, customers, bills, payments]);

  const chequeTotal = useMemo(
    () => form.cheques.reduce((s, c) => s + (Number(c.amount) || 0), 0),
    [form.cheques],
  );

  const cashTotal = Number(form.cashAmount) || 0;
  const cdmTotal = form.cdmDeposits.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const onlineTransferTotal = form.onlineTransfers.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const paymentTotal = Math.round((cashTotal + chequeTotal + cdmTotal + onlineTransferTotal) * 100) / 100;

  const allocatedTotal = useMemo(() => {
    let sum = 0;
    for (const bill of pendingBills) {
      sum += Number(form.billAllocations[bill.id]) || 0;
    }
    return Math.round(sum * 100) / 100;
  }, [pendingBills, form.billAllocations]);

  const unallocatedTotal = Math.round((paymentTotal - allocatedTotal) * 100) / 100;
  const lockDateToToday = mustUseTodayRecordDate();

  const handleChange = (field, value) => {
    if (field === 'date' && lockDateToToday) return;
    if (field === 'billNumber') {
      receiptNumberTouched.current = true;
      setForm((f) => ({ ...f, billNumber: String(value).slice(0, 40) }));
      return;
    }
    if (field === 'customerId') {
      const defaults = lastChequeDefaultsForCustomer(payments, value);
      const bankDefaults = lastOtherMethodBankDefaults(payments, value);
      setForm((f) => ({
        ...f,
        customerId: value,
        billAllocations: {},
        cdmDeposits: [
          newCdmLine({ bankAccountId: bankDefaults.cdmBankAccountId || f.cdmDeposits[0]?.bankAccountId || '' }),
        ],
        onlineTransfers: [
          newOnlineTransferLine({
            bankAccountId: bankDefaults.onlineTransferBankAccountId || f.onlineTransfers[0]?.bankAccountId || '',
          }),
        ],
        cheques: [newChequeLine(defaults || {})],
      }));
      return;
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleBillAllocationChange = (billId, value) => {
    const id = String(billId ?? '').trim();
    if (!id) return;
    setForm((f) => ({
      ...f,
      billAllocations: { ...f.billAllocations, [id]: value },
    }));
  };

  const handleChequeChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      cheques: f.cheques.map((c) => {
        if (c.key !== key) return c;
        const next = { ...c, [field]: value };
        if (field === 'chequeBank') {
          const code = bankCodeForName(value);
          if (code) next.chequeBankCode = code;
        }
        return next;
      }),
    }));
  };

  const addChequeLine = () => {
    setForm((f) => {
      const template = f.cheques[0];
      const defaults = template
        ? {
            chequeBank: template.chequeBank,
            chequeBankCode: template.chequeBankCode,
            chequeBranchCode: template.chequeBranchCode,
            chequeDate: template.chequeDate,
          }
        : {};
      return { ...f, cheques: [...f.cheques, newChequeLine(defaults)] };
    });
  };

  const removeChequeLine = (key) => {
    setForm((f) => {
      const next = f.cheques.filter((c) => c.key !== key);
      return { ...f, cheques: next.length > 0 ? next : [newChequeLine()] };
    });
  };

  const handleCdmChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      cdmDeposits: f.cdmDeposits.map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const addCdmLine = () => {
    setForm((f) => {
      const template = f.cdmDeposits[0];
      return {
        ...f,
        cdmDeposits: [...f.cdmDeposits, newCdmLine({ bankAccountId: template?.bankAccountId || '', cdmDate: template?.cdmDate || todayYmdLocal() })],
      };
    });
  };

  const removeCdmLine = (key) => {
    setForm((f) => {
      const next = f.cdmDeposits.filter((line) => line.key !== key);
      return { ...f, cdmDeposits: next.length > 0 ? next : [newCdmLine()] };
    });
  };

  const handleOnlineTransferChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      onlineTransfers: f.onlineTransfers.map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const addOnlineTransferLine = () => {
    setForm((f) => {
      const template = f.onlineTransfers[0];
      return {
        ...f,
        onlineTransfers: [
          ...f.onlineTransfers,
          newOnlineTransferLine({
            bankAccountId: template?.bankAccountId || '',
            transferDate: template?.transferDate || todayYmdLocal(),
          }),
        ],
      };
    });
  };

  const removeOnlineTransferLine = (key) => {
    setForm((f) => {
      const next = f.onlineTransfers.filter((line) => line.key !== key);
      return { ...f, onlineTransfers: next.length > 0 ? next : [newOnlineTransferLine()] };
    });
  };

  const lockedCustomerLabel =
    customerName ||
    customers.find((c) => c.id === form.customerId)?.name ||
    '—';

  const validateStep1 = () => {
    if (!form.customerId) {
      setSaveError('Select a shop.');
      return false;
    }
    const receiptNumber = normalizePaymentReceiptInput(form.billNumber);
    if (!receiptNumber) {
      setSaveError('Enter a payment receipt # (letters and/or numbers).');
      return false;
    }
    if (!/^[A-Za-z0-9][A-Za-z0-9 \-._/]*$/.test(receiptNumber)) {
      setSaveError('Receipt # can use letters, numbers, spaces, and . _ - /');
      return false;
    }
    if (paymentTotal <= 0) {
      setSaveError('Enter a cash, cheque, CDM deposit, and/or online transfer amount so the total is greater than 0.');
      return false;
    }
    for (let i = 0; i < form.cdmDeposits.length; i++) {
      const line = form.cdmDeposits[i];
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!String(line.cdmNumber).trim()) {
        setSaveError(`CDM deposit ${i + 1}: enter a CDM number.`);
        return false;
      }
      if (!String(line.bankAccountId).trim()) {
        setSaveError(`CDM deposit ${i + 1}: select a bank account.`);
        return false;
      }
      if (!line.cdmDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.cdmDate)) {
        setSaveError(`CDM deposit ${i + 1}: enter a valid deposit date.`);
        return false;
      }
    }
    for (let i = 0; i < form.onlineTransfers.length; i++) {
      const line = form.onlineTransfers[i];
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!String(line.reference).trim()) {
        setSaveError(`Online transfer ${i + 1}: enter a transfer reference number.`);
        return false;
      }
      if (!String(line.bankAccountId).trim()) {
        setSaveError(`Online transfer ${i + 1}: select a bank account.`);
        return false;
      }
      if (!line.transferDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.transferDate)) {
        setSaveError(`Online transfer ${i + 1}: enter a valid transfer date.`);
        return false;
      }
    }
    for (let i = 0; i < form.cheques.length; i++) {
      const line = form.cheques[i];
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!line.chequeDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.chequeDate)) {
        setSaveError(`Cheque ${i + 1}: enter a valid conversion date.`);
        return false;
      }
      if (!String(line.chequeNumber).trim()) {
        setSaveError(`Cheque ${i + 1}: enter a cheque number.`);
        return false;
      }
    }
    if (
      payments.some(
        (r) => String(r.billNumber || '').trim() === receiptNumber,
      )
    ) {
      setSaveError('This payment receipt number is already used.');
      return false;
    }
    setSaveError(null);
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (newPaymentsLocked) {
      setSaveError('Collection for this day is over. You cannot add more payments.');
      return;
    }
    if (!validateStep1()) return;
    setStep(2);
  };

  const handleBack = () => {
    setSaveError(null);
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = getUsername();
    if (!username) {
      setSaveError('You need to be signed in with a username.');
      return;
    }
    if (newPaymentsLocked) {
      setSaveError('Collection for this day is over. You cannot add more payments.');
      return;
    }
    if (!validateStep1()) {
      setStep(1);
      return;
    }

    const receiptNumber = normalizePaymentReceiptInput(form.billNumber);
    const billCashAllocations = [];
    for (const bill of pendingBills) {
      const amount = Number(form.billAllocations[bill.id]) || 0;
      if (amount <= 0) continue;
      if (amount > bill.outstandingAmount + 0.009) {
        setSaveError(
          bill.isOpeningBalance
            ? `Amount for opening balance cannot exceed ${money(bill.outstandingAmount)} outstanding.`
            : `Amount for bill ${bill.invoiceNumber || bill.billDate || bill.id} cannot exceed ${money(bill.outstandingAmount)} outstanding.`,
        );
        return;
      }
      billCashAllocations.push({ billId: bill.id, cashAmount: amount });
    }
    if (billCashAllocations.length === 0) {
      setSaveError('Enter an amount for at least one opening balance or pending invoice.');
      return;
    }
    if (Math.abs(allocatedTotal - paymentTotal) > 0.009) {
      setSaveError(
        `Allocated total (${money(allocatedTotal)}) must equal payment total (${money(paymentTotal)}).`,
      );
      return;
    }

    const chequeLines = [];
    for (const line of form.cheques) {
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      chequeLines.push({
        amount,
        chequeDate: line.chequeDate,
        chequeNumber: String(line.chequeNumber).trim(),
        chequeBank: String(line.chequeBank).trim(),
        chequeBankCode: String(line.chequeBankCode).trim(),
        chequeBranchCode: String(line.chequeBranchCode).trim(),
      });
    }
    const cdmLines = [];
    for (const line of form.cdmDeposits) {
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      cdmLines.push({
        amount,
        cdmNumber: String(line.cdmNumber).trim(),
        cdmDate: line.cdmDate,
        bankAccountId: String(line.bankAccountId).trim(),
      });
    }
    const onlineLines = [];
    for (const line of form.onlineTransfers) {
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      onlineLines.push({
        amount,
        reference: String(line.reference).trim(),
        transferDate: line.transferDate,
        bankAccountId: String(line.bankAccountId).trim(),
      });
    }

    setSaving(true);
    setSaveError(null);
    try {
      const res = await authFetch(`${apiBase}/api/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: form.customerId,
          billNumber: receiptNumber,
          cashAmount: cashTotal,
          cdmDeposits: cdmLines,
          onlineTransfers: onlineLines,
          cheques: chequeLines,
          billCashAllocations,
          date: lockDateToToday ? todayYmdLocal() : form.date,
          note: form.note.trim(),
          recordedBy: username,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || 'Save failed');
        return;
      }
      if (onSaved) await onSaved(data);
      onClose();
    } catch {
      setSaveError('Could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collector-bill-settlement-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={`${modalPanelClass} flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-none flex-col overflow-hidden !p-0 sm:max-w-xl lg:max-w-3xl`}
      >
        <div className="shrink-0 border-b border-slate-100 px-4 pb-3 pt-3 sm:px-6 sm:pt-6">
          <div
            className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-slate-300/90 sm:hidden"
            aria-hidden
          />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="collector-bill-settlement-title" className="text-lg font-bold text-slate-900 sm:text-xl">
                {step === 1 ? 'Record payment' : 'Allocate to invoices'}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {step === 1
                  ? 'Select the shop and enter cash or cheque payment details.'
                  : 'Split the payment across opening balance and pending invoices. You can settle any of them in any order — partial amounts are allowed.'}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
              Step {step} of 2
            </span>
          </div>
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={step === 1 ? handleNext : handleSubmit}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
            <div className="space-y-4">
              {newPaymentsLocked ? (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-950 ring-1 ring-amber-100" role="status">
                  Collection for today is over. You cannot add more payments.
                </p>
              ) : null}
              {saveError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100">
                  {saveError}
                </p>
              ) : null}

              {step === 1 ? (
                <>
                  {lockCustomer ? (
                    <div className="block text-sm font-medium text-slate-600">
                      Shop
                      <p className="mt-1 rounded-xl bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-900 ring-1 ring-slate-200">
                        {lockedCustomerLabel}
                      </p>
                    </div>
                  ) : (
                    <label className="block text-sm font-medium text-slate-600">
                      Shop
                      <select
                        required
                        value={form.customerId}
                        onChange={(e) => handleChange('customerId', e.target.value)}
                        className={inputClass}
                        disabled={customers.length === 0}
                      >
                        <option value="">{customers.length === 0 ? 'No shops yet' : 'Select shop…'}</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-600">
                      Payment receipt #
                      <input
                        type="text"
                        autoComplete="off"
                        required
                        maxLength={40}
                        value={form.billNumber}
                        onChange={(e) => handleChange('billNumber', e.target.value)}
                        className={`${inputClass} font-mono`}
                        placeholder="e.g. PAY-012"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-600">
                      Payment date
                      {lockDateToToday ? (
                        <>
                          <p
                            className="mt-1 flex min-h-[2.75rem] items-center rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm tabular-nums text-slate-700 ring-1 ring-slate-200"
                            aria-readonly="true"
                          >
                            {todayYmdLocal()}
                            <span className="sr-only"> (today, cannot be changed)</span>
                          </p>
                          <span className="mt-1 block text-xs font-normal text-slate-500">
                            Today only — this date cannot be changed.
                          </span>
                        </>
                      ) : (
                        <input
                          type="date"
                          required
                          value={form.date}
                          onChange={(e) => handleChange('date', e.target.value)}
                          className={inputClass}
                        />
                      )}
                    </label>
                  </div>
                  <label className="block text-sm font-medium text-slate-600">
                    Cash (LKR)
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.cashAmount}
                      onChange={(e) => handleChange('cashAmount', e.target.value)}
                      className={`${inputClass} tabular-nums`}
                      placeholder="0"
                    />
                  </label>
                  <fieldset className="rounded-xl bg-sky-50/70 p-3 ring-1 ring-sky-100 sm:p-4">
                    <legend className="px-1 text-sm font-semibold text-slate-800">CDM deposits</legend>
                    <div className="mt-1 flex flex-wrap items-start justify-between gap-2">
                      <p className="text-xs text-slate-500">
                        Amount, deposit date, CDM number, and shop bank account. Credited when a manager approves.
                      </p>
                      <button
                        type="button"
                        onClick={addCdmLine}
                        className="shrink-0 rounded-lg border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 shadow-sm transition hover:bg-sky-50"
                      >
                        + Add CDM
                      </button>
                    </div>
                    <div className="mt-3 space-y-3">
                      {form.cdmDeposits.map((line, index) => (
                        <div key={line.key} className="rounded-xl bg-white/80 p-3 ring-1 ring-sky-100 sm:p-4">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              CDM {index + 1}
                            </p>
                            {form.cdmDeposits.length > 1 ? (
                              <button
                                type="button"
                                onClick={() => removeCdmLine(line.key)}
                                className="text-xs font-medium text-slate-500 hover:text-rose-600"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label className="block text-sm font-medium text-slate-600">
                              Amount (LKR)
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                value={line.amount}
                                onChange={(e) => handleCdmChange(line.key, 'amount', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                                placeholder="0"
                              />
                            </label>
                            <label className="block text-sm font-medium text-slate-600">
                              Deposit date <span className="text-rose-600">*</span>
                              <input
                                type="date"
                                value={line.cdmDate}
                                onChange={(e) => handleCdmChange(line.key, 'cdmDate', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              />
                            </label>
                            <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                              CDM number <span className="text-rose-600">*</span>
                              <input
                                type="text"
                                autoComplete="off"
                                value={line.cdmNumber}
                                onChange={(e) => handleCdmChange(line.key, 'cdmNumber', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                                placeholder="e.g. CDM receipt / slip no."
                              />
                            </label>
                          </div>
                          <label className="mt-3 block text-sm font-medium text-slate-600">
                            Bank account <span className="text-rose-600">*</span>
                            <select
                              value={line.bankAccountId}
                              onChange={(e) => handleCdmChange(line.key, 'bankAccountId', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              disabled={bankAccounts.length === 0}
                            >
                              <option value="">
                                {bankAccounts.length === 0 ? 'No accounts — add under Shop' : 'Select account…'}
                              </option>
                              {bankAccounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {bankAccountOptionLabel(a)}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>
                  <fieldset className="rounded-xl bg-teal-50/70 p-3 ring-1 ring-teal-100 sm:p-4">
                    <legend className="px-1 text-sm font-semibold text-slate-800">Online transfers</legend>
                    <div className="mt-1 flex flex-wrap items-start justify-between gap-2">
                      <p className="text-xs text-slate-500">
                        Amount, transfer date, bank reference, and shop bank account. Credited when a manager approves.
                      </p>
                      <button
                        type="button"
                        onClick={addOnlineTransferLine}
                        className="shrink-0 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-800 shadow-sm transition hover:bg-teal-50"
                      >
                        + Add transfer
                      </button>
                    </div>
                    <div className="mt-3 space-y-3">
                      {form.onlineTransfers.map((line, index) => (
                        <div key={line.key} className="rounded-xl bg-white/80 p-3 ring-1 ring-teal-100 sm:p-4">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Transfer {index + 1}
                            </p>
                            {form.onlineTransfers.length > 1 ? (
                              <button
                                type="button"
                                onClick={() => removeOnlineTransferLine(line.key)}
                                className="text-xs font-medium text-slate-500 hover:text-rose-600"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label className="block text-sm font-medium text-slate-600">
                              Amount (LKR)
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                value={line.amount}
                                onChange={(e) => handleOnlineTransferChange(line.key, 'amount', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                                placeholder="0"
                              />
                            </label>
                            <label className="block text-sm font-medium text-slate-600">
                              Transfer date <span className="text-rose-600">*</span>
                              <input
                                type="date"
                                value={line.transferDate}
                                onChange={(e) => handleOnlineTransferChange(line.key, 'transferDate', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              />
                            </label>
                            <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                              Transfer reference # <span className="text-rose-600">*</span>
                              <input
                                type="text"
                                autoComplete="off"
                                value={line.reference}
                                onChange={(e) => handleOnlineTransferChange(line.key, 'reference', e.target.value)}
                                className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                                placeholder="e.g. bank ref / transaction ID"
                              />
                            </label>
                          </div>
                          <label className="mt-3 block text-sm font-medium text-slate-600">
                            Bank account <span className="text-rose-600">*</span>
                            <select
                              value={line.bankAccountId}
                              onChange={(e) => handleOnlineTransferChange(line.key, 'bankAccountId', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              disabled={bankAccounts.length === 0}
                            >
                              <option value="">
                                {bankAccounts.length === 0 ? 'No accounts — add under Shop' : 'Select account…'}
                              </option>
                              {bankAccounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {bankAccountOptionLabel(a)}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">Cheques</p>
                      <button
                        type="button"
                        onClick={addChequeLine}
                        className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                      >
                        + Add cheque
                      </button>
                    </div>
                    {form.cheques.map((line, index) => (
                      <div
                        key={line.key}
                        className="rounded-xl bg-slate-50/90 p-4 ring-1 ring-slate-100"
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Cheque {index + 1}
                          </p>
                          {form.cheques.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeChequeLine(line.key)}
                              className="text-xs font-medium text-slate-500 hover:text-rose-600"
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm font-medium text-slate-600">
                            Amount (LKR)
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={line.amount}
                              onChange={(e) => handleChequeChange(line.key, 'amount', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="0"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600">
                            Conversion date
                            <input
                              type="date"
                              value={line.chequeDate}
                              onChange={(e) => handleChequeChange(line.key, 'chequeDate', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            />
                          </label>
                        </div>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm font-medium text-slate-600">
                            Bank
                            <select
                              value={line.chequeBank}
                              onChange={(e) => handleChequeChange(line.key, 'chequeBank', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            >
                              <option value="">Select bank…</option>
                              {SRI_LANKA_BANKS.map((b) => (
                                <option key={`${b.code}-${b.name}`} value={b.name}>
                                  {b.name} ({b.code})
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm font-medium text-slate-600">
                            Bank code
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              maxLength={4}
                              value={line.chequeBankCode}
                              onChange={(e) =>
                                handleChequeChange(line.key, 'chequeBankCode', e.target.value.replace(/\D/g, '').slice(0, 4))
                              }
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="e.g. 7056"
                            />
                          </label>
                        </div>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm font-medium text-slate-600">
                            Branch code
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              maxLength={3}
                              value={line.chequeBranchCode}
                              onChange={(e) =>
                                handleChequeChange(
                                  line.key,
                                  'chequeBranchCode',
                                  e.target.value.replace(/\D/g, '').slice(0, 3),
                                )
                              }
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="e.g. 080"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600">
                            Cheque number
                            <input
                              type="text"
                              autoComplete="off"
                              value={line.chequeNumber}
                              onChange={(e) => handleChequeChange(line.key, 'chequeNumber', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="e.g. 123456"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="flex flex-col gap-1 text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-baseline">
                    <span>
                      Total payment:{' '}
                      <span className="font-semibold tabular-nums text-slate-900">{money(paymentTotal)}</span>
                    </span>
                    {chequeTotal > 0 || cdmTotal > 0 || onlineTransferTotal > 0 ? (
                      <span className="text-xs text-slate-500 sm:ml-2">
                        (
                        {[
                          cashTotal > 0 ? `cash ${money(cashTotal)}` : null,
                          chequeTotal > 0 ? `cheques ${money(chequeTotal)}` : null,
                          cdmTotal > 0 ? `CDM ${money(cdmTotal)}` : null,
                          onlineTransferTotal > 0 ? `online ${money(onlineTransferTotal)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        )
                      </span>
                    ) : cashTotal > 0 ? (
                      <span className="text-xs text-slate-500 sm:ml-2">(cash only)</span>
                    ) : null}
                  </p>
                  <label className="block text-sm font-medium text-slate-600">
                    Note (optional)
                    <input
                      type="text"
                      value={form.note}
                      onChange={(e) => handleChange('note', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Collection route, remarks…"
                    />
                  </label>
                </>
              ) : (
                <>
                  <div className="rounded-xl bg-indigo-50/90 p-4 ring-1 ring-indigo-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Payment total</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-indigo-950">{money(paymentTotal)}</p>
                    <p className="mt-1 text-sm text-indigo-800/90">
                      {[
                        cashTotal > 0 ? `Cash ${money(cashTotal)}` : null,
                        chequeTotal > 0 ? `Cheques ${money(chequeTotal)}` : null,
                        cdmTotal > 0 ? `CDM ${money(cdmTotal)}` : null,
                        onlineTransferTotal > 0 ? `Online ${money(onlineTransferTotal)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </p>
                    <p className="mt-2 text-sm text-indigo-700">
                      Shop: <span className="font-semibold">{lockedCustomerLabel}</span>
                    </p>
                  </div>

                  <div className="rounded-xl bg-slate-50/90 p-3 ring-1 ring-slate-100 sm:p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">Opening balance & invoices</p>
                      <p className="text-xs tabular-nums text-slate-600">
                        Allocated {money(allocatedTotal)} of {money(paymentTotal)}
                        {Math.abs(unallocatedTotal) > 0.009 ? (
                          <span className={unallocatedTotal > 0 ? ' text-amber-700' : ' text-rose-700'}>
                            {' '}
                            · {unallocatedTotal > 0 ? `${money(unallocatedTotal)} left` : `${money(Math.abs(unallocatedTotal))} over`}
                          </span>
                        ) : (
                          <span className=" text-emerald-700"> · Fully allocated</span>
                        )}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      Enter how much of this payment applies to the opening balance and each invoice. Settle any of them in any order — partial amounts are allowed.
                    </p>
                    {pendingBills.length === 0 ? (
                      <p className="mt-3 text-sm text-slate-500">
                        No outstanding opening balance or credit invoices for this shop.
                      </p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {pendingBills.map((b) => (
                          <li
                            key={b.id}
                            className={`rounded-lg px-3 py-3 sm:flex sm:items-center sm:gap-4 ${
                              b.isOpeningBalance
                                ? 'bg-amber-50/80 ring-1 ring-amber-200'
                                : 'bg-white ring-1 ring-slate-200'
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900">
                                {b.isOpeningBalance
                                  ? 'Opening balance'
                                  : b.invoiceNumber
                                    ? `Inv ${b.invoiceNumber}`
                                    : b.billDate || '—'}
                                {b.isOpeningBalance ? (
                                  <span className="ml-2 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                                    Invoice
                                  </span>
                                ) : null}
                              </p>
                              <p className="mt-0.5 text-xs tabular-nums text-slate-500">
                                {[
                                  b.billDate || null,
                                  b.dueDate ? `due ${b.dueDate}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </p>
                              <p className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-800">
                                {money(b.outstandingAmount)} due
                                {b.billTotal > 0 ? (
                                  <span className="ml-1 text-xs font-normal text-slate-500">
                                    of {money(b.billTotal)}
                                  </span>
                                ) : null}
                              </p>
                              {!b.isOpeningBalance && b.details ? (
                                <p className="mt-1 text-xs leading-snug text-slate-600">{b.details}</p>
                              ) : null}
                            </div>
                            <label className="mt-3 block shrink-0 text-sm font-medium text-slate-600 sm:mt-0 sm:w-36">
                              Amount (LKR)
                              <input
                                type="number"
                                min={0}
                                max={b.outstandingAmount}
                                step={0.01}
                                value={form.billAllocations[b.id] ?? ''}
                                onChange={(e) => handleBillAllocationChange(b.id, e.target.value)}
                                className={`mt-1 w-full rounded-xl border-0 px-3 py-2.5 text-sm tabular-nums ring-1 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 ${
                                  b.isOpeningBalance
                                    ? 'bg-white ring-amber-200'
                                    : 'bg-slate-100 ring-slate-200'
                                }`}
                                placeholder="0"
                              />
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="shrink-0 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-sm sm:px-6 sm:py-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              {step === 2 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={saving}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 sm:w-auto sm:py-2.5"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 sm:w-auto sm:py-2.5"
                >
                  Cancel
                </button>
              )}
              {step === 1 ? (
                <button
                  type="submit"
                  disabled={customers.length === 0 || paymentTotal <= 0 || newPaymentsLocked}
                  className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-md disabled:opacity-60 sm:w-auto sm:py-2.5"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={
                    saving ||
                    newPaymentsLocked ||
                    pendingBills.length === 0 ||
                    paymentTotal <= 0 ||
                    Math.abs(unallocatedTotal) > 0.009 ||
                    allocatedTotal <= 0
                  }
                  className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-md disabled:opacity-60 sm:w-auto sm:py-2.5"
                >
                  {saving ? 'Saving…' : 'Save payment'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
